import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useCurrentTenant } from '_graphql/cache/auth';

/**
 * Color palette shades for CSS custom properties
 * Maps shade number to lightness adjustment
 */
const COLOR_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

interface TenantBranding {
  logo: string | null;
  primaryColor: string | null;
  name: string | null;
  isReady: boolean;
  isLogoLoaded: boolean;
}

interface TenantBrandingContextValue extends TenantBranding {
  preloadLogo: (url: string) => Promise<boolean>;
}

const defaultBranding: TenantBrandingContextValue = {
  logo: null,
  primaryColor: null,
  name: null,
  isReady: false,
  isLogoLoaded: false,
  preloadLogo: async () => false,
};

const TenantBrandingContext =
  createContext<TenantBrandingContextValue>(defaultBranding);

/**
 * Convert hex color to HSL
 */
function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex
  let r: number, g: number, b: number;

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255;
    g = parseInt(hex[1] + hex[1], 16) / 255;
    b = parseInt(hex[2] + hex[2], 16) / 255;
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16) / 255;
    g = parseInt(hex.slice(2, 4), 16) / 255;
    b = parseInt(hex.slice(4, 6), 16) / 255;
  } else {
    return null;
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to RGB string for CSS custom properties
 */
function hslToRGBString(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  // Return as space-separated RGB values (for Tailwind CSS)
  return `${Math.round((r + m) * 255)} ${Math.round((g + m) * 255)} ${Math.round((b + m) * 255)}`;
}

/**
 * Generate color palette from a primary color
 * Returns RGB values for each shade
 */
function generateColorPalette(
  primaryColor: string
): Record<number, string> | null {
  const hsl = hexToHSL(primaryColor);
  if (!hsl) return null;

  const { h, s } = hsl;

  // Generate shades by adjusting lightness
  // Shade 500 is the base color
  const lightnessMap: Record<number, number> = {
    50: 97,
    100: 94,
    200: 86,
    300: 77,
    400: 66,
    500: 50, // Base
    600: 45,
    700: 30,
    800: 22,
    900: 15,
  };

  const palette: Record<number, string> = {};

  for (const shade of COLOR_SHADES) {
    const lightness = lightnessMap[shade];
    // Reduce saturation for very light shades
    const adjustedSaturation = shade <= 100 ? Math.max(s - 20, 10) : s;
    palette[shade] = hslToRGBString(h, adjustedSaturation, lightness);
  }

  return palette;
}

/**
 * Apply color palette to document as CSS custom properties
 */
function applyColorPalette(palette: Record<number, string> | null): void {
  const root = document.documentElement;

  if (!palette) {
    // Reset to default (remove custom properties)
    for (const shade of COLOR_SHADES) {
      root.style.removeProperty(`--color-primary-${shade}`);
    }
    return;
  }

  for (const shade of COLOR_SHADES) {
    root.style.setProperty(`--color-primary-${shade}`, palette[shade]);
  }
}

/**
 * Preload an image and return a promise
 */
function preloadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

interface TenantBrandingProviderProps {
  children: ReactNode;
  /** Show children even before branding is ready (default: false) */
  showWhileLoading?: boolean;
  /** Custom loading component */
  loadingComponent?: ReactNode;
  /** Timeout for branding to be ready (ms, default: 3000) */
  timeout?: number;
}

/**
 * TenantBrandingProvider
 *
 * Provides tenant-specific branding (logo, colors) to the application.
 * Prevents flash of unstyled content by waiting for branding to load.
 *
 * Features:
 * - Generates color palette from tenant's primary_color
 * - Injects CSS custom properties for theme colors
 * - Preloads tenant logo to prevent layout shift
 * - Provides loading state and timeout fallback
 */
export const TenantBrandingProvider: FC<TenantBrandingProviderProps> = ({
  children,
  showWhileLoading = false,
  loadingComponent,
  timeout = 3000,
}) => {
  const currentTenant = useCurrentTenant();
  const [branding, setBranding] = useState<TenantBranding>({
    logo: null,
    primaryColor: null,
    name: null,
    isReady: false,
    isLogoLoaded: false,
  });

  const appliedTenantIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const preloadLogo = useCallback(async (url: string): Promise<boolean> => {
    return preloadImage(url);
  }, []);

  // Apply branding when tenant changes
  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // No tenant yet - mark as ready with defaults
    if (!currentTenant) {
      applyColorPalette(null);
      setBranding({
        logo: null,
        primaryColor: null,
        name: null,
        isReady: true,
        isLogoLoaded: true,
      });
      return;
    }

    // Same tenant - skip
    if (appliedTenantIdRef.current === currentTenant.id) {
      return;
    }

    appliedTenantIdRef.current = currentTenant.id;

    const { logo, primary_color, name } = currentTenant;

    // Apply color palette immediately
    if (primary_color) {
      const palette = generateColorPalette(primary_color);
      applyColorPalette(palette);
    } else {
      applyColorPalette(null);
    }

    // Set initial branding state
    setBranding({
      logo: logo || null,
      primaryColor: primary_color || null,
      name: name || null,
      isReady: false,
      isLogoLoaded: false,
    });

    // Start timeout for fallback
    timeoutRef.current = setTimeout(() => {
      setBranding((prev) => ({
        ...prev,
        isReady: true,
        isLogoLoaded: prev.isLogoLoaded,
      }));
    }, timeout);

    // Preload logo
    if (logo) {
      preloadImage(logo).then((loaded) => {
        setBranding((prev) => ({
          ...prev,
          isLogoLoaded: loaded,
          isReady: true,
        }));

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      });
    } else {
      // No logo - mark as ready
      setBranding((prev) => ({
        ...prev,
        isLogoLoaded: true,
        isReady: true,
      }));

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentTenant, timeout]);

  const contextValue: TenantBrandingContextValue = {
    ...branding,
    preloadLogo,
  };

  // Show loading state if not ready and not showing while loading
  if (!branding.isReady && !showWhileLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    // Default loading state - minimal branded loader
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          {branding.logo && branding.isLogoLoaded ? (
            <img
              src={branding.logo}
              alt={branding.name || 'Loading'}
              className="h-12 w-auto animate-pulse"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-primary-500 animate-pulse" />
          )}
          <div className="h-1 w-32 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full w-full origin-left animate-[loading_1s_ease-in-out_infinite] bg-primary-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <TenantBrandingContext.Provider value={contextValue}>
      {children}
    </TenantBrandingContext.Provider>
  );
};

/**
 * Hook to access tenant branding
 */
export const useTenantBranding = (): TenantBrandingContextValue => {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error(
      'useTenantBranding must be used within a TenantBrandingProvider'
    );
  }
  return context;
};

export default TenantBrandingProvider;
